import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthProvider";
import { useGetMe } from "@/queries/me";
import { getWebSocketUrl } from "@/utils/reverb-config";
import { NOTIFICATIONS_QUERY_KEY } from "@/queries/notifications";
import axios from "@/utils/axios";

type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

interface ReverbMessage {
  event: string;
  channel?: string;
  data: string;
}

interface UseReverbOptions {
  enabled?: boolean;
  onNotification?: (notification: unknown) => void;
  onFlowDuplicationCompleted?: (payload: unknown) => void;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
}

/**
 * Hook for connecting to Laravel Reverb WebSocket server.
 * Uses refs to avoid stale closure issues with WebSocket callbacks.
 */
export const useReverb = (options: UseReverbOptions = {}) => {
  const {
    enabled = true,
    onNotification,
    onFlowDuplicationCompleted,
    autoReconnect = true,
    maxReconnectAttempts = 5,
  } = options;

  const { authenticated, keycloak } = useAuth();
  const { data: meData } = useGetMe();
  const queryClient = useQueryClient();

  // Get user ID from useGetMe
  const userId = meData?.data?.id;

  // Use refs to always have access to latest values in WebSocket callbacks
  const userIdRef = useRef(userId);
  const onNotificationRef = useRef(onNotification);
  const onFlowDuplicationCompletedRef = useRef(onFlowDuplicationCompleted);

  // Update refs when values change
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onFlowDuplicationCompletedRef.current = onFlowDuplicationCompleted;
  }, [onFlowDuplicationCompleted]);

  // WebSocket and connection state refs
  const wsRef = useRef<WebSocket | null>(null);
  const socketIdRef = useRef<string | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isConnectingRef = useRef(false);

  const [status, setStatus] = useState<ConnectionStatus>("disconnected");

  // Subscribe to private channel
  const subscribeToChannel = useCallback(
    async (channelName: string, socketId: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.error("[Reverb] WebSocket not open, cannot subscribe");
        return;
      }

      try {
        // Use configured axios instance - it handles auth token and baseURL automatically
        // The /broadcasting path is detected by axios interceptor to skip X-Requested-With header
        const response = await axios.post(
          "/broadcasting/auth",
          new URLSearchParams({
            channel_name: channelName,
            socket_id: socketId,
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );

        const { auth } = response.data;

        // Send subscription message - data MUST be stringified JSON
        ws.send(
          JSON.stringify({
            event: "pusher:subscribe",
            data: JSON.stringify({
              channel: channelName,
              auth,
            }),
          }),
        );

        console.log(`[Reverb] Subscribed to channel: ${channelName}`);
      } catch (error) {
        console.error("[Reverb] Subscription error:", error);
      }
    },
    [],
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      if (wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close(1000, "Client cleanup");
      }
      wsRef.current = null;
    }
    socketIdRef.current = null;
    isConnectingRef.current = false;
  }, []);

  // Disconnect
  const disconnect = useCallback(() => {
    cleanup();
    setStatus("disconnected");
  }, [cleanup]);

  // Ref for connect function to avoid circular dependency
  const connectRef = useRef<() => void>(() => {});

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (!autoReconnect) return;

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error("[Reverb] Max reconnection attempts reached");
      setStatus("error");
      return;
    }

    reconnectAttemptsRef.current += 1;
    const delay = Math.min(
      1000 * Math.pow(2, reconnectAttemptsRef.current - 1),
      30000,
    );

    console.log(
      `[Reverb] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      // Check if still should connect
      if (userIdRef.current) {
        connectRef.current();
      }
    }, delay);
  }, [autoReconnect, maxReconnectAttempts]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const currentUserId = userIdRef.current;

    if (!authenticated || !currentUserId) {
      console.log(
        "[Reverb] Not authenticated or no user ID, skipping connection",
      );
      return;
    }

    // Prevent duplicate connection attempts
    if (isConnectingRef.current) {
      console.log("[Reverb] Connection already in progress");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("[Reverb] Already connected");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log("[Reverb] Already connecting");
      return;
    }

    // Cleanup any existing connection
    cleanup();

    isConnectingRef.current = true;
    setStatus("connecting");

    const wsUrl = getWebSocketUrl();
    console.log("[Reverb] Connecting to:", wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log(
        "[Reverb] WebSocket connection opened, waiting for server handshake...",
      );
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const message: ReverbMessage = JSON.parse(event.data);
        console.log("[Reverb] Received message:", message);
        switch (message.event) {
          case "auth.logout":
          case ".auth.logout": {
            console.log("[Reverb] Received auth.logout event, logging out");
            keycloak?.logout();
            break;
          }
          case "pusher:connection_established": {
            const data = JSON.parse(message.data);
            const socketId = data.socket_id;
            socketIdRef.current = socketId;
            isConnectingRef.current = false;
            reconnectAttemptsRef.current = 0;
            setStatus("connected");

            console.log("[Reverb] Connected with socket_id:", socketId);

            // Subscribe to user's notification channel using user ID from useGetMe
            const currentUserId = userIdRef.current;
            if (currentUserId) {
              const channelName = `private-notifications.${currentUserId}`;
              subscribeToChannel(channelName, socketId);
            } else {
              console.error("[Reverb] No user ID available for subscription");
            }
            break;
          }

          case "pusher_internal:subscription_succeeded":
            console.log(
              "[Reverb] Successfully subscribed to:",
              message.channel,
            );
            break;

          case "pusher:error": {
            const errorData = JSON.parse(message.data);
            console.error("[Reverb] Server error:", errorData);
            setStatus("error");
            break;
          }

          case "pusher:pong":
            // Keep-alive response
            break;

          case "notification.created":
          case ".notification.created": {
            const notification = JSON.parse(message.data);
            console.log("[Reverb] New notification received:", notification);

            // Invalidate notifications query
            queryClient.invalidateQueries({
              queryKey: [NOTIFICATIONS_QUERY_KEY],
            });

            // Call callback if provided (from ref to get latest)
            onNotificationRef.current?.(notification);
            break;
          }

          case "flow.duplication.completed":
          case ".flow.duplication.completed": {
            const payload = JSON.parse(message.data);
            console.log("[Reverb] Flow duplication completed:", payload);

            // Invalidate flow-related queries so lists refresh
            queryClient.invalidateQueries({
              queryKey: ["flows"],
            });
            queryClient.invalidateQueries({
              queryKey: ["main-flows"],
            });

            // Call callback if provided
            onFlowDuplicationCompletedRef.current?.(payload);
            break;
          }

          default:
            console.log("[Reverb] Unhandled event:", message.event, message);
        }
      } catch (error) {
        console.error("[Reverb] Failed to parse message:", error, event.data);
      }
    };

    ws.onerror = (error) => {
      console.error("[Reverb] WebSocket error:", error);
      isConnectingRef.current = false;
      setStatus("error");
    };

    ws.onclose = (event) => {
      console.log("[Reverb] WebSocket closed:", event.code, event.reason);
      isConnectingRef.current = false;
      socketIdRef.current = null;
      setStatus("disconnected");

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Schedule reconnection if not intentional close (code 1000)
      if (event.code !== 1000) {
        scheduleReconnect();
      }
    };

    // Setup keep-alive ping every 30 seconds
    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ event: "pusher:ping", data: {} }));
      }
    }, 30000);
  }, [
    authenticated,
    cleanup,
    keycloak,
    queryClient,
    scheduleReconnect,
    subscribeToChannel,
  ]);

  // Update connect ref for use in scheduleReconnect
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Effect: Connect/disconnect based on enabled and authentication
  useEffect(() => {
    if (!enabled || !authenticated || !userId) {
      disconnect();
      return;
    }

    // Small delay to ensure auth state is stable
    const timeoutId = setTimeout(() => {
      connect();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      disconnect();
    };
  }, [enabled, authenticated, userId, connect, disconnect]);

  return {
    status,
    isConnected: status === "connected",
    connect,
    disconnect,
  };
};
